import { CloudPod, ProductFeature, PlanFamily, Permission, EmulatorType } from '@localstack/types';
import {
  ButtonWithDialog,
  PageTitle,
  Dropdown,
  LoadingFragment,
  PodsTable,
  UsageLimit,
  FeatureUnavailableBox,
} from '@localstack/ui';
import { Card, CardContent, CardHeader, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { ReactElement, useMemo, useState } from 'react';

import {
  MembershipService,
  CloudPodsService,
  formatBytes,
  useApiEffect,
  useApiGetter,
  useRoutes,
  useSnackbar,
} from '@localstack/services';
import { ExternalLink } from '@localstack/constants';

import { AppRoute } from '~/config';
import { pricingRoute } from '~/util/pricing';
import { ContainedCustomerLayout } from '~/layouts';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { getUserInfo } from '~/util/storage';

import { CloudPodsSettings } from './CloudPodsSettings';
import { getUserName } from './components/utils';

export const CloudPods = (): ReactElement => {
  const userInfo = getUserInfo();
  const { goto } = useRoutes();
  const { can, hasFeature } = useAuthProvider();
  const { showSnackbar } = useSnackbar();
  const [selectedPodIds, setSelectedPodIds] = useState<string[]>([]);
  const [onlyUserPods, setOnlyUserPods] = useState<boolean>(false);

  const canAccessPods = hasFeature(ProductFeature.FEATURE_PODS);

  const {
    data: allPods,
    isLoading: isPodLoading,
    mutate,
  } = useApiGetter(CloudPodsService, 'listCloudPods', [], { defaultValue: [], enable: canAccessPods });

  const { deleteCloudPod, isLoading: isPodDeleting } = useApiEffect(CloudPodsService, ['deleteCloudPod'], {
    revalidate: ['listCloudPods'],
  });

  const { patchCloudPod } = useApiEffect(CloudPodsService, ['patchCloudPod'], { revalidate: ['listCloudPods'] });

  const { data: cloudPodsStats, isLoading: isStatsLoading } = useApiGetter(CloudPodsService, 'getCloudPodsStats', [], {
    enable: canAccessPods,
  });

  // Members only resolve owner names in the pods table, which does not render without pods access.
  const { data: members } = useApiGetter(MembershipService, 'listMembers', [], {
    defaultValue: [],
    enable: canAccessPods,
  });

  const selectedPods = useMemo(
    () => allPods?.filter((pod) => selectedPodIds.includes(pod.id)) ?? [],
    [selectedPodIds, allPods],
  );

  const onDeletePods = async () => {
    selectedPods.map(async (pod) => {
      await deleteCloudPod(pod.pod_name);
    });
    showSnackbar({ severity: 'success', message: 'Successfully deleted selected cloud pods' });
  };

  const onMakePodsPublic = async () => {
    selectedPods.map(async (pod) => {
      await patchCloudPod(pod.pod_name, { ...pod, is_public: true });
    });
    showSnackbar({ severity: 'success', message: 'Successfully made selected cloud pods public' });
  };

  const onMakePodsPrivate = async () => {
    selectedPods.map(async (pod) => {
      await patchCloudPod(pod.pod_name, { ...pod, is_public: false });
    });
    showSnackbar({ severity: 'success', message: 'Successfully made selected cloud pods private' });
  };

  const pods = useMemo(
    () =>
      (allPods ?? [])
        .filter((pod) => !pod.pod_name.match(/^ci[:_].+/))
        .filter((pod) => !onlyUserPods || pod.user_id === userInfo?.user.id),
    [allPods, onlyUserPods],
  );
  const isLoading = isPodLoading || isPodDeleting;

  const podsWithUsername = useMemo(
    () => pods.map((pod) => ({ ...pod, user_id: getUserName(members, pod.user_id ?? '') })),
    [pods],
  );

  const orgStorageSize = cloudPodsStats?.org_pods?.storage_size ?? 0;
  const userPodsDetails = (cloudPodsStats?.user_pods || {})[userInfo?.user.id || ''];
  const userStorageSize = userPodsDetails?.storage_size ?? 0;
  const maxStorage = cloudPodsStats?.org_limit?.storage_size || 500 * 1024 * 1024;
  return (
    <ContainedCustomerLayout title="Cloud Pods">
      {canAccessPods ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <LoadingFragment loading={isStatsLoading} variant="card" height={184}>
              <Card>
                <CardHeader title="Cloud Pods Storage" />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      Organization Storage ({formatBytes(maxStorage)}):
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <UsageLimit
                        variant="edged"
                        greenRange={[0, 75]}
                        orangeRange={[75, 90]}
                        redRange={[90, 100]}
                        value={Math.min((orgStorageSize / maxStorage) * 100, 100)}
                        displayValue={formatBytes(orgStorageSize)}
                        tooltip={`Used ${formatBytes(orgStorageSize)} of organization cloud pods storage`}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      User Storage ({formatBytes(maxStorage)}):
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <UsageLimit
                        variant="edged"
                        greenRange={[0, 75]}
                        orangeRange={[75, 90]}
                        redRange={[90, 100]}
                        value={Math.min((userStorageSize / maxStorage) * 100, 100)}
                        displayValue={formatBytes(userStorageSize)}
                        tooltip={`Used ${formatBytes(userStorageSize)} of user cloud pods storage`}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </LoadingFragment>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={<PageTitle title="Cloud Pods" onMutate={mutate} typographyVariant="h5" />}
                action={
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={onlyUserPods}
                          onChange={(_, checked) => setOnlyUserPods(checked)}
                        />
                      }
                      label="Show my pods only"
                    />
                    <Dropdown label="Actions">
                      <ButtonWithDialog
                        componentType="MenuItem"
                        color="primary"
                        disabled={selectedPodIds.length === 0}
                        onClick={() => onDeletePods()}
                        dialogTitle="Do you want to delete the following cloud pods? This action cannot be undone!"
                        dialogContent={
                          <>
                            {selectedPods.map((pod) => (
                              <Typography key={pod.id}>{pod.pod_name}</Typography>
                            ))}
                          </>
                        }
                      >
                        Delete
                      </ButtonWithDialog>
                      <ButtonWithDialog
                        componentType="MenuItem"
                        color="primary"
                        disabled={selectedPodIds.length === 0}
                        onClick={() => onMakePodsPublic()}
                        dialogTitle="Make following cloud pods public?"
                        dialogContent={
                          <>
                            {selectedPods.map((pod) => (
                              <Typography key={pod.id}>{pod.pod_name}</Typography>
                            ))}
                          </>
                        }
                      >
                        Make public
                      </ButtonWithDialog>
                      <ButtonWithDialog
                        componentType="MenuItem"
                        color="primary"
                        disabled={selectedPodIds.length === 0}
                        onClick={() => onMakePodsPrivate()}
                        dialogTitle="Make following cloud pods private?"
                        dialogContent={
                          <>
                            {selectedPods.map((pod) => (
                              <Typography key={pod.id}>{pod.pod_name}</Typography>
                            ))}
                          </>
                        }
                      >
                        Make private
                      </ButtonWithDialog>
                    </Dropdown>
                  </>
                }
              />
              <PodsTable
                pods={(podsWithUsername as CloudPod[]) || []}
                loading={isLoading}
                onViewPod={(name) => goto(AppRoute.POD, { name })}
                onSelect={setSelectedPodIds}
              />
            </Card>
          </Grid>
          {can(Permission.UPDATE_ORGANIZATION) && (
            <Grid item xs={12}>
              <CloudPodsSettings />
            </Grid>
          )}
        </Grid>
      ) : (
        <FeatureUnavailableBox
          icon="cloudpods"
          availableIn={{ family: PlanFamily.BASE_PLANS, emulators: [EmulatorType.AWS] }}
          upgradeLink={pricingRoute([EmulatorType.AWS])}
          docsLink={ExternalLink.DOCS_CLOUD_PODS}
          title="Cloud Pods"
          description="Cloud Pods are persistent and shareable state snapshots for LocalStack. Version your infrastructure, debug with your team, and automate test setups with consistent, reproducible, pre-seeded environments—locally or in CI."
        />
      )}
    </ContainedCustomerLayout>
  );
};
